import React from 'react';
import { Button } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatLocalizedQuantity } from '../../../../utils/numberFormatting';
import { formatUnit } from '../../../../utils/units';
import type { OrderDeliveryMode } from '../../../transports/transportDirection';
import type { OrderDraftState } from '../draft/orderDraftModel';
import type { OrderStepBar, OrderStepPrimaryAction } from '../orderStepBar';
import type { OrderStepContext } from '../orderStepContext';
import OrderGuideBelowRow from './OrderGuideBelowRow';
import OrderGuidePanel from './OrderGuidePanel';
import OrderPlanAside from './OrderPlanAside';
import { getOrderProductDisplayDimensions, withLineDimensions } from '../../orderProductDisplay';
import OrderStepGuide from './OrderStepGuide';
import { activeSupplierDraft } from '../orderProcessFacts';
import {
  getOrderGuideDraftFactsFromDraft,
  getOrderGuideDraftFactsFromOrder,
  isOrderGuideDataComplete,
  toOrderGuideLine,
} from './orderGuideFacts';
import {
  getConfirmedGuideItems,
  getConfirmedPrepaymentItems,
  getDraftGuideItems,
  getDraftProformaItems,
  getGuideHelpKey,
  getGuidePrimaryLabelKey,
  getOfferGuideItems,
  type OrderGuideItem,
  type OrderGuidePrepaymentFacts,
  type OrderGuideStepKey,
} from './orderGuideRules';

export interface OrderGuideScreenProps {
  step: OrderGuideStepKey;
  ctx: OrderStepContext;
  /** Hint and main action of the step (`orderStepBar.ts`) — the card shows them in place of the sticky bar. */
  bar: OrderStepBar;
  /** The step is a finished one shown for reading: title and help only. */
  previewOnly: boolean;
  busy: boolean;
  onPrimary: (action: OrderStepPrimaryAction) => void;
  /** Szkic being edited on the card: the editor's draft (otherwise the saved order is read). */
  draft?: OrderDraftState;
  /** „Ceny sprzedaży” can be opened (a saved draft the role may price). */
  canOpenPrices?: boolean;
  onOpenPrices?: () => void;
  /** „Zmień status ręcznie” and the step buttons that come before the main one (Zapisz szkic). */
  manualStatus?: React.ReactNode;
  secondary?: React.ReactNode;
  /** Potwierdzone: „Wyślij potwierdzenie do klienta”. */
  onSendCustomerConfirmation?: () => void;
  onDeliveryModeChange?: (mode: OrderDeliveryMode) => void;
  savingDelivery?: boolean;
  /** Szkic with unsaved line edits: the margin card shows the last saved plan and says so. */
  marginStale?: boolean;
  /** What the step shows between the process panel and the margin row (the editor, the offer). */
  children?: React.ReactNode;
  /** Oferta / Potwierdzone of a ZK paid on a proforma: its rows and the buttons („Dodaj proformę”, „Oznacz wpłatę”, „Dodaj fakturę zaliczkową”). */
  prepayment?: OrderGuidePrepaymentFacts | null;
  prepaymentActions?: React.ReactNode;
}

/**
 * Steps 1–3 of the ZK card as in the first mockup „Karta zamówienia ZK”: the
 * process panel (step card with checklist and buttons + „Sposób realizacji”),
 * the step's own content, and the row with the margin card and „Po stronie
 * Valentyny”. The page supplies facts and commands; the rules are in
 * `orderStepGuide.ts` (rows) and `orderStepBar.ts` (the main action).
 */
export default function OrderGuideScreen({
  step,
  ctx,
  bar,
  previewOnly,
  busy,
  onPrimary,
  draft,
  canOpenPrices = false,
  onOpenPrices,
  manualStatus,
  secondary,
  onSendCustomerConfirmation,
  onDeliveryModeChange,
  savingDelivery = false,
  marginStale = false,
  children,
  prepayment = null,
  prepaymentActions = null,
}: OrderGuideScreenProps) {
  const { t, i18n } = useTranslation(['orders']);
  const { order, caps } = ctx;
  const offerSent = order.status !== 'NEW_INQUIRY';
  const factoryDirect = order.fulfillmentSource === 'FACTORY_DIRECT';
  const facts = draft && step === 'DRAFT'
    ? getOrderGuideDraftFactsFromDraft(draft, { canViewSalePrices: caps.canViewSalePrices, canOpenPrices })
    : getOrderGuideDraftFactsFromOrder(order, { canViewSalePrices: caps.canViewSalePrices });

  let items: OrderGuideItem[] | null = null;
  const productsById = new Map((order.productsLoaded ? order.products : []).map((product) => [product.id, product]));
  if (!previewOnly) {
    if (step === 'DRAFT') items = [...getDraftGuideItems(facts), ...getDraftProformaItems(prepayment)];
    else if (step === 'OFFER') items = getOfferGuideItems({ dataComplete: isOrderGuideDataComplete(facts), offerSent, prepayment });
    else {
      const zd = factoryDirect ? activeSupplierDraft(ctx.stockStatus) : null;
      items = [...getConfirmedPrepaymentItems(prepayment), ...getConfirmedGuideItems({
        factoryDirect,
        factoryZd: zd ? {
          number: zd.supplierOrderNumber || '',
          ordered: zd.state === 'REVIEWED' && Boolean(zd.supplierOrderStatus) && zd.supplierOrderStatus !== 'DRAFT',
          truckPlanned: ctx.transports.length > 0 || (zd.transports?.length ?? 0) > 0,
        } : null,
        stockLines: ctx.stockStatus
          ? ctx.stockStatus.lines.map((line) => ({
            name: withLineDimensions(line.name, productsById.get(line.orderProductId)),
            unit: line.unit, state: line.state, ordered: line.ordered, onHand: line.onHand, missing: line.missing,
          }))
          : null,
        partialLines: (ctx.stockStatus?.supplierDraft?.partialLines ?? []).map((line) => ({
          name: withLineDimensions(line.name, productsById.get(line.orderProductId)),
          unit: line.unit, ordered: line.orderedQuantity, covered: line.coveredQuantity, missing: line.remainingQuantity,
        })),
        format: {
          number: (value) => (value == null ? '—' : formatLocalizedQuantity(value, i18n.language)),
          quantity: (value, unit) => (value == null ? '—' : `${formatLocalizedQuantity(value, i18n.language)} ${formatUnit(unit || 'pcs', i18n.language)}`),
        },
      })];
    }
  }

  // The main action waits only where the bar says so (a draft without lines or prices); the rows above say what is missing.
  const hintText = t(bar.hint.key, bar.hint.params);
  // Straight from the factory the whole quantity is ordered anyway: a „brakuje towaru” warning would only mislead.
  const stockWarningMoot = factoryDirect && step === 'CONFIRMED' && !bar.primaryDisabled && !bar.manualOnly;
  const showHint = Boolean(!stockWarningMoot && (bar.primaryDisabled || bar.hint.tone === 'warning' || bar.manualOnly));
  const hint = items && showHint ? { tone: bar.hint.tone, text: hintText } : null;

  const primary = bar.primary && !previewOnly
    ? {
      labelKey: getGuidePrimaryLabelKey(bar.primary),
      disabled: bar.primaryDisabled,
      busy,
      onClick: () => bar.primary && onPrimary(bar.primary),
    }
    : null;

  const lineNames = (draft && step === 'DRAFT' ? facts.lines : (order.productsLoaded ? order.products : []).map((line) => toOrderGuideLine(line)))
    .map((line) => line.name);
  // Same lines in the same order as `lineNames` (facts.lines maps draft.lines one to one).
  const lineDimensions = (draft && step === 'DRAFT' ? draft.lines : (order.productsLoaded ? order.products : []))
    .map((line) => getOrderProductDisplayDimensions(line));
  const deliveryMode: OrderDeliveryMode = draft && step === 'DRAFT'
    ? draft.header.deliveryMode
    : order.deliveryMode === 'CUSTOMER_PICKUP' ? 'CUSTOMER_PICKUP' : 'OUR_DELIVERY';

  const proformaButtons = (step === 'OFFER' || step === 'CONFIRMED') && !previewOnly && prepayment ? prepaymentActions : null;
  const stepSecondary = secondary || proformaButtons ? <>{secondary}{proformaButtons}</> : null;

  const sendConfirmation = step === 'CONFIRMED' && !previewOnly && caps.canSendCustomerConfirmation && onSendCustomerConfirmation && (
    <Button icon={<MailOutlined />} onClick={onSendCustomerConfirmation} data-testid="zk-guide-send-confirmation">
      {t('orders:process.confirmed.sendToCustomer')}
    </Button>
  );

  return (
    <>
      <OrderGuidePanel
        manualStatus={manualStatus}
        guide={(
          <OrderStepGuide
            step={step}
            helpKey={getGuideHelpKey(step, offerSent, factoryDirect)}
            items={items}
            primary={primary}
            onOpenPrices={canOpenPrices ? onOpenPrices : undefined}
            secondary={stepSecondary || sendConfirmation ? <>{stepSecondary}{sendConfirmation}</> : null}
            hint={hint}
          />
        )}
        aside={step === 'CONFIRMED' ? (
          <OrderPlanAside
            step={step}
            deliveryMode={deliveryMode}
            lineNames={lineNames}
            lineDimensions={lineDimensions}
            canChangeDelivery={!previewOnly && caps.canEditDeliveryMode}
            onDeliveryModeChange={onDeliveryModeChange}
            savingDelivery={savingDelivery}
            fulfillmentSource={order.fulfillmentSource}
            fulfillmentNote={order.fulfillmentNote}
            transports={caps.canViewTransports ? ctx.transports : null}
            transportsLoading={ctx.transportsLoading}
            orderId={order.id}
            canViewCosts={caps.canViewTransportCosts}
          />
        ) : undefined}
      />
      {children}
      {/* „Marża zamówienia” starts at Potwierdzone: Szkic and Oferta keep the margin chip in the header only (owner, 2026-10-05). */}
      {step === 'CONFIRMED' && <div data-testid="zk-step-CONFIRMED"><OrderGuideBelowRow ctx={ctx} marginStale={marginStale} /></div>}
    </>
  );
}
